// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Engine-scope overview band + cylinder summary band + sensitivity control:
// - cylinderSummaryRows / updateCylinderSummaryLayout / scheduleCylinderSummaryLayout:
//   measure card layout and collapse a single orphan row.
// - clearOverviewReviewPanelHeight / updateOverviewReviewPanelHeight /
//   scheduleOverviewReviewPanelHeight / setupOverviewReviewPanelHeightSync:
//   clears legacy right-rail review sizing now that review context sits inline.
// - currentEngineDescriptors: thin wrapper that resolves engine descriptors
//   from the current parser metadata + selected flight.
// - activateEngineScopeCard / bindEngineScopeCards: scope card click/keyboard.
// - renderMultiEngineOverview: aircraft + per-engine + delta snapshot cards.
// - renderCylinderScopeSummary: per-cylinder system-card band.
// - setDetectionSensitivity / renderSensitivityControl: detection-sensitivity UI.

export function createEngineScopeRenderer({
  escapeHtml,
  scoreClass,
  sensitivityModes,
  engineDescriptorsForData,
  aircraftProfileForEntry,
  normalizeSensitivity,
  engineSnapshotCard,
  aircraftSnapshotCard,
  engineDeltaOverviewCard,
  normalizeSelectedEngineScope,
  engineInfoForCylinderNumber,
  cylinderMetricSummary,
  cylinderScoreMarkup,
  setEngineScope,
  clearCylinderFocus,
  selectCompanionSeriesForHealthCard,
  renderHealthSummary,
  scheduleDrawAll,
  reviewMonitorEnabled,
  saveDetectionSensitivity,
  syncSensitivityRoute = () => {},
  updateHealthSummary,
  renderTrendsForSelection,
  getCylinderSummaryLayoutScheduled,
  setCylinderSummaryLayoutScheduled,
  getOverviewReviewPanelHeightScheduled,
  setOverviewReviewPanelHeightScheduled,
  getOverviewReviewPanelResizeObserver,
  setOverviewReviewPanelResizeObserver,
  getSelectedCylinderNumber,
  getSelectedHealthKey,
  setSelectedHealthKey,
  setOverviewSelectionCleared = () => {},
  getSelectedFlight,
  getFlightData,
  getHealthSummary,
  getDetectionSensitivity,
  setDetectionSensitivity: setDetectionSensitivityGlobal,
  getCurrentParserMetadata,
  setExplorerSelectedAlertId,
}) {
  function cylinderSummaryRows(band) {
    const children = [...band.children].filter((child) => !child.hidden);
    if (!children.length) return [];
    const rows = [];
    children.forEach((child) => {
      const top = Math.round(child.offsetTop);
      const row = rows.find((candidate) => Math.abs(candidate.top - top) <= 2);
      if (row) {
        row.items.push(child);
      } else {
        rows.push({ top, items: [child] });
      }
    });
    return rows.sort((a, b) => a.top - b.top).map((row) => row.items);
  }

  function updateCylinderSummaryLayout() {
    setCylinderSummaryLayoutScheduled(false);
    const band = document.querySelector("#cylinderSummaryBand");
    if (!band || band.hidden) return;
    const children = [...band.children].filter((child) => !child.hidden);
    if (!children.length) return;
    band.classList.remove("is-one-row-compressed");
    band.style.removeProperty("--cylinder-summary-columns");
    const rows = cylinderSummaryRows(band);
    const hasSingleOrphanRow = rows.length === 2 && rows[1]?.length === 1;
    if (!hasSingleOrphanRow) return;
    band.style.setProperty("--cylinder-summary-columns", String(children.length));
    band.classList.add("is-one-row-compressed");
  }

  function scheduleCylinderSummaryLayout() {
    if (getCylinderSummaryLayoutScheduled()) return;
    setCylinderSummaryLayoutScheduled(true);
    requestAnimationFrame(updateCylinderSummaryLayout);
  }

  function preserveScrollDuring(callback) {
    if (typeof window === "undefined") return callback();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const result = callback();
    window.scrollTo({ top: scrollY, left: scrollX, behavior: "auto" });
    requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: scrollX, behavior: "auto" }));
    return result;
  }

  function clearOverviewReviewPanelHeight() {
    const grid = document.querySelector(".analysis-grid.overview-only");
    if (grid) {
      grid.style.removeProperty("--overview-review-panel-height");
      grid.style.removeProperty("--overview-review-panel-offset");
    }
  }

  function updateOverviewReviewPanelHeight() {
    setOverviewReviewPanelHeightScheduled(false);
    clearOverviewReviewPanelHeight();
  }

  function scheduleOverviewReviewPanelHeight() {
    if (getOverviewReviewPanelHeightScheduled()) return;
    setOverviewReviewPanelHeightScheduled(true);
    requestAnimationFrame(updateOverviewReviewPanelHeight);
  }

  function setupOverviewReviewPanelHeightSync() {
    if (getOverviewReviewPanelResizeObserver() || typeof ResizeObserver !== "function") {
      scheduleOverviewReviewPanelHeight();
      return;
    }
    const grid = document.querySelector(".analysis-grid.overview-only");
    const mainColumn = grid?.querySelector(".analysis-main-column");
    if (!mainColumn) return;
    const observer = new ResizeObserver(scheduleOverviewReviewPanelHeight);
    observer.observe(mainColumn);
    setOverviewReviewPanelResizeObserver(observer);
    scheduleOverviewReviewPanelHeight();
  }

  function currentEngineDescriptors(data = getFlightData()) {
    const selectedFlight = getSelectedFlight();
    const currentParserMetadata = getCurrentParserMetadata();
    return engineDescriptorsForData(data, aircraftProfileForEntry(selectedFlight), {
      parserId: currentParserMetadata?.parserId ?? selectedFlight?.parser,
      parserMetadata: currentParserMetadata,
    });
  }

  function activateEngineScopeCard(scope, summary = getHealthSummary()) {
    if (!scope) return;
    setOverviewSelectionCleared(false);
    if (scope === "aircraft") {
      setSelectedHealthKey(null);
      clearCylinderFocus();
      return;
    }
    if (scope === "compare") {
      const selectedFlight = getSelectedFlight();
      if (reviewMonitorEnabled("engineDelta", selectedFlight?.aircraft)) {
        setSelectedHealthKey("engineDelta");
        selectCompanionSeriesForHealthCard(getSelectedHealthKey());
      }
      setEngineScope("compare");
      return;
    }
    setSelectedHealthKey(null);
    setEngineScope(scope);
  }

  function bindEngineScopeCards(container, summary = getHealthSummary()) {
    container.querySelectorAll("[data-engine-scope-card]").forEach((card) => {
      const activate = () => activateEngineScopeCard(card.dataset.engineScopeCard, summary);
      card.addEventListener("click", activate);
      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        activate();
      });
    });
  }

  function renderMultiEngineOverview(summary) {
    const containers = [
      document.querySelector("#multiEngineOverview"),
      document.querySelector("#healthMultiEngineOverview"),
    ].filter(Boolean);
    if (!containers.length) return;
    const engines = currentEngineDescriptors();
    const scope = normalizeSelectedEngineScope(engines);
    const overviewActive = document.querySelector('.tab[data-panel="overview"]')?.classList.contains("is-active") ?? true;
    const selectedCylinderNumber = getSelectedCylinderNumber();
    const shouldHide = engines.length <= 1 || selectedCylinderNumber || scope === "left" || scope === "right" || scope === "compare";
    containers.forEach((container) => {
      const isDashboardOverview = container.id === "multiEngineOverview";
      if ((isDashboardOverview && !overviewActive) || shouldHide) {
        container.hidden = true;
        container.innerHTML = "";
        container.classList.remove("is-scoped");
        return;
      }
      const left = engines[0];
      const right = engines[1];
      container.classList.remove("is-scoped");
      container.hidden = false;
      container.innerHTML = `
        ${aircraftSnapshotCard(summary, { active: true })}
        ${engineSnapshotCard(left)}
        ${engineSnapshotCard(right)}
        ${engineDeltaOverviewCard(left, right)}
      `;
      bindEngineScopeCards(container, summary);
    });
  }

  function renderCylinderScopeSummary(summary) {
    const engineBand = document.querySelector(".summary-band.overview-only:not(#cylinderSummaryBand)");
    const cylinderBand = document.querySelector("#cylinderSummaryBand");
    const overviewScoreRow = document.querySelector("#overviewScoreRow");
    if (!engineBand || !cylinderBand) return;
    const overviewActive = document.querySelector('.tab[data-panel="overview"]')?.classList.contains("is-active") ?? true;
    if (!overviewActive) {
      if (overviewScoreRow) overviewScoreRow.hidden = true;
      engineBand.hidden = true;
      cylinderBand.hidden = true;
      cylinderBand.innerHTML = "";
      cylinderBand.classList.remove("is-one-row-compressed");
      cylinderBand.style.removeProperty("--cylinder-summary-columns");
      return;
    }
    const selectedCylinderNumber = getSelectedCylinderNumber();
    if (!selectedCylinderNumber) {
      if (overviewScoreRow) {
        const engines = currentEngineDescriptors();
        const scope = normalizeSelectedEngineScope(engines);
        overviewScoreRow.hidden = engines.length <= 1 || scope === "left" || scope === "right" || scope === "compare";
      }
      engineBand.hidden = false;
      cylinderBand.hidden = true;
      cylinderBand.innerHTML = "";
      cylinderBand.classList.remove("is-one-row-compressed");
      cylinderBand.style.removeProperty("--cylinder-summary-columns");
      return;
    }
    const cylinderInfo = engineInfoForCylinderNumber(selectedCylinderNumber, currentEngineDescriptors());
    const metric = cylinderMetricSummary(selectedCylinderNumber, summary);
    if (overviewScoreRow) overviewScoreRow.hidden = true;
    engineBand.hidden = true;
    cylinderBand.hidden = false;
    const cards = [
      ["Probe Confidence", metric.probeScore, metric.probeNote, "amber", "!", "sensors"],
      ["CHT Heat", metric.chtScore, metric.chtNote, "green", "♥", "cht"],
      ["Cooling Rate", metric.coolingRateScore, metric.coolingRateNote, "amber", "↡", "coolingRate"],
      ["EGT Behavior", metric.egtScore, metric.egtNote, "amber", "≋", "egt"],
      ...(metric.inductionLeakFlagged ? [["Induction Leak", metric.inductionLeakScore, metric.inductionLeakNote, "amber", "↘", "inductionLeak"]] : []),
      ["EGT Rhythm", metric.valveScore, metric.valveNote, "purple", "⌁", "valve"],
      ["EGT/CHT Pairing", metric.pairingScore, metric.pairingNote, "purple", "⌁", "combustion"],
      ["Peer Comparison", metric.peerScore, metric.peerNote, "blue", "∴", "cooling"],
      ["Context", metric.contextScore, metric.contextNote, "blue", "ϟ", ""],
    ];
    cylinderBand.innerHTML = `
      <div class="score-card cylinder-score-card">
        <div>
          <p class="score-label">${escapeHtml(cylinderInfo.label)} Health</p>
          <strong class="${metric.className}">${metric.score}</strong>
          <span class="${metric.className}">${metric.label}</span>
        </div>
      </div>
      ${cards.map(([label, score, note, iconClass, icon, healthKey]) => {
        const tag = healthKey ? "button" : "div";
        const attrs = healthKey
          ? `data-health-card="${escapeHtml(healthKey)}" data-cylinder-health-card="${escapeHtml(healthKey)}" type="button"`
          : "";
        return `
        <${tag} class="system-card" ${attrs}>
          <span class="system-icon ${iconClass}" aria-hidden="true">${icon}</span>
          <div>
            <p>${escapeHtml(label)}</p>
            <strong class="${scoreClass(score)}">${cylinderScoreMarkup(score)}</strong>
            <small>${escapeHtml(note)}</small>
          </div>
        </${tag}>
      `;
      }).join("")}
    `;
    cylinderBand.querySelectorAll("[data-cylinder-health-card]").forEach((card) => {
      card.addEventListener("click", () => {
        const key = card.dataset.cylinderHealthCard;
        const selectedFlight = getSelectedFlight();
        if (!key || !reviewMonitorEnabled(key, selectedFlight?.aircraft)) return;
        setOverviewSelectionCleared(false);
        setSelectedHealthKey(key);
        selectCompanionSeriesForHealthCard(getSelectedHealthKey());
        preserveScrollDuring(() => renderHealthSummary(summary));
        scheduleDrawAll();
      });
    });
    scheduleCylinderSummaryLayout();
  }

  let sensitivityRecomputeTimer = null;
  function scheduleSensitivityRecompute() {
    if (sensitivityRecomputeTimer) clearTimeout(sensitivityRecomputeTimer);
    sensitivityRecomputeTimer = setTimeout(() => {
      sensitivityRecomputeTimer = null;
      if (!getFlightData()?.length) {
        renderSensitivityControl(getHealthSummary());
        return;
      }
      updateHealthSummary();
      scheduleDrawAll();
      if (document.querySelector("#view-trends")?.classList.contains("is-active")) {
        renderTrendsForSelection();
      }
    }, 120);
  }

  function setDetectionSensitivity(value) {
    // Read-only public share (issue #100): sensitivity is locked to the owner's
    // shared value. Ignore any stray change (click/keyboard) so a viewer cannot
    // alter what produced the findings, even if the control re-renders enabled.
    if (typeof document !== "undefined" && document.body?.hasAttribute("data-public-share")) return;
    const next = normalizeSensitivity(value);
    const current = getDetectionSensitivity();
    if (next === current) {
      renderSensitivityControl(getHealthSummary());
      return;
    }
    setDetectionSensitivityGlobal(next);
    setExplorerSelectedAlertId(null);
    saveDetectionSensitivity(next);
    // Reflect the new selection in the control immediately so the active
    // button/select stays responsive even while we debounce the expensive
    // recompute below (rapid arrow-key navigation through sensitivity
    // options would otherwise rebuild every finding card on each step).
    renderSensitivityControl(getHealthSummary());
    syncSensitivityRoute(next);
    scheduleSensitivityRecompute();
  }

  function renderSensitivityControl(summary = getHealthSummary()) {
    const detectionSensitivity = getDetectionSensitivity();
    const mode = sensitivityModes[detectionSensitivity];
    const select = document.querySelector("#sensitivityControl");
    if (select) {
      select.value = detectionSensitivity;
      select.title = mode.note;
    }
    document.querySelectorAll("[data-sensitivity]").forEach((button) => {
      const isActive = button.dataset.sensitivity === detectionSensitivity;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-checked", String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });

    const note = document.querySelector("#sensitivityNote");
    if (note) note.textContent = mode.note;
  }

  return {
    cylinderSummaryRows,
    updateCylinderSummaryLayout,
    scheduleCylinderSummaryLayout,
    clearOverviewReviewPanelHeight,
    updateOverviewReviewPanelHeight,
    scheduleOverviewReviewPanelHeight,
    setupOverviewReviewPanelHeightSync,
    currentEngineDescriptors,
    activateEngineScopeCard,
    bindEngineScopeCards,
    renderMultiEngineOverview,
    renderCylinderScopeSummary,
    setDetectionSensitivity,
    renderSensitivityControl,
  };
}
